import { matchPath, NavLink, useLocation, useNavigate } from "react-router-dom"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import SymbolWhite from "@/assets/symbol-orange.svg"
import LogoHorizontalWhite from "@/assets/logo-horizontal-orange.svg"
import PatternBg2 from "@/assets/pattern-bg-2.png"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { useChat } from "@/modules/Chat/context/ChatContext"
import { useEffect, useRef, useState } from "react"
import { FeedbackModal } from "@/modules/Feedback/components/FeedbackModal"

interface SidebarItem {
  title: string
  url?: string
  icon?: string
  allowedRoles?: number[]
  isDivider?: boolean
  adminRoute?: boolean
  action?: string
}

const sidebarItems: SidebarItem[] = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: "chart-pie-alt",
    allowedRoles: [6, 5, 4, 3, 2, 1],
  },
  {
    title: "Eventos",
    url: "/events",
    icon: "calendar",
    allowedRoles: [6, 5, 4, 3, 2, 1],
  },
  {
    title: "Minhas Inscrições",
    url: "/my-subscriptions",
    icon: "bullseye-pointer",
    allowedRoles: [6, 5, 4, 3, 2, 1],
  },
  {
    title: "Conteúdos",
    url: "/reprises",
    icon: "clapperboard-play",
    allowedRoles: [6, 5, 4, 3, 2, 1],
  },
  {
    title: "Membro",
    isDivider: true,
    allowedRoles: [3, 5],
  },
  {
    title: "Network",
    url: "/network",
    icon: "network-analytic",
    allowedRoles: [3, 5],
  },
  {
    title: "Mentorias",
    url: "/mentoring",
    icon: "handshake",
    allowedRoles: [3, 5],
  },
  // Plano de Ação oculto do backoffice.
  // {
  //   title: "Plano de Ação (3 Q`s)",
  //   url: "/company-action-plans",
  //   icon: "plan",
  //   allowedRoles: [3, 5],
  // },
  {
    title: "Palestras",
    url: "/lectures",
    icon: "chalkboard-user",
    allowedRoles: [3, 5],
  },
  {
    title: "Eventos Adicionais",
    url: "/overdelivery",
    icon: "bonus-alt",
    allowedRoles: [3, 5],
  },
  {
    title: "Administração",
    isDivider: true,
    adminRoute: true,
  },
  {
    title: "Usuários",
    url: "/users",
    icon: "users",
    adminRoute: true,
  },
  {
    title: "Gestão de Inscrições",
    url: "/subscriptions",
    icon: "login",
    adminRoute: true,
  },
  {
    title: "Progresso",
    url: "/progresso",
    icon: "bars-progress",
    adminRoute: true,
  },
  {
    title: "Logs",
    url: "/logs",
    icon: "search-alt",
    adminRoute: true,
  },
]

interface BackofficeSidebarProps {
  userRole?: string
  userName?: string
}

export function BackofficeSidebar({
  userRole = "user",
  userName = "Admin",
}: BackofficeSidebarProps) {
  const { state, setOpenMobile, isMobile } = useSidebar()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { isOpen: isChatOpen, toggleChat } = useChat()
  const collapsed = state === "collapsed"
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const { pathname } = useLocation()

  const handleLinkClick = (index: number) => {
    setActiveIndex(index)
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const getNavCls = (path: string) => {
    return matchPath({ path, end: false }, pathname)
      ? "bg-[#2b2b2b] text-primary font-semibold"
      : "text-muted-foreground hover:bg-surface-hover hover:text-foreground"
  }

  function checkIsActive(path: string) {
    return matchPath({ path, end: false }, pathname)
  }

  const handleLogout = () => {
    logout()
    navigate("/login")
  }

  const SidebarDivider = ({ title }: { title?: string }): JSX.Element => {
    return (
      <div className="my-3 flex items-center gap-0">
        <hr className="w-full border-orange-500" />
        <small className="min-w-[60%] rounded-full border border-orange-500 bg-orange-500 px-2 text-center font-black uppercase tracking-wider text-[#FFF]">
          {title}
        </small>
        <hr className="w-full border-orange-500" />
      </div>
    )
  }

  const [activeIndex, setActiveIndex] = useState(0)
  const itemsRef = useRef([])
  const [indicatorStyle, setIndicatorStyle] = useState({
    top: 0,
    height: 0,
  })

  useEffect(() => {
    const activeItemIndex = sidebarItems.findIndex(
      (item) => item.url && matchPath({ path: item.url, end: false }, pathname)
    )
    if (activeItemIndex !== -1) {
      setActiveIndex(activeItemIndex)
    }
  }, [pathname])

  useEffect(() => {
    const activeItem = itemsRef.current[activeIndex]

    if (activeItem)
      setIndicatorStyle({ top: activeItem.offsetTop, height: activeItem.offsetHeight })
  }, [activeIndex, sidebarItems])

  return (
    <>
      <Sidebar
        collapsible="icon"
        className="z-50"
      >
        <SidebarContent
          className="flex flex-col bg-surface"
          style={{
            backgroundImage: `url(${PatternBg2})`,
            backgroundSize: "300px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "bottom right",
          }}
        >
          <div className="flex flex-grow flex-col justify-between overflow-hidden">
            <div className="p-6 group-data-[collapsible=icon]:!p-2">
              <div className="flex items-center gap-3 group-data-[collapsible=icon]:!justify-center">
                {!collapsed ? (
                  <img
                    src={LogoHorizontalWhite}
                    alt="Símbolo Giants"
                    width="500px"
                  />
                ) : (
                  <img
                    src={SymbolWhite}
                    alt="Logotipo Giants"
                    width="50px"
                  />
                )}
              </div>
            </div>

            <SidebarGroup className="relative flex-1 overflow-auto px-4 [mask-image:linear-gradient(to_bottom,transparent_0%,black_5%,black_95%,transparent_100%)]">
              <SidebarGroupContent>
                <SidebarMenu>
                  {sidebarItems.map((item, index) => {
                    // 1. Validação de Admin: Se o item é admin e o user não é admin, esconde.
                    if (item.adminRoute && !user.pesAdmin) {
                      return null
                    }

                    const isAllowedRole = item.allowedRoles?.includes(user.pesTipo)
                    const isSpecialCompany = user.empId === 198

                    if (item.allowedRoles && !isAllowedRole && !isSpecialCompany) {
                      return null
                    }

                    // Se passou pelos filtros, renderiza:
                    if (item.isDivider) {
                      return (
                        <SidebarDivider
                          title={item.title}
                          key={item.title}
                        />
                      )
                    }

                    if (item.action === "openChat") {
                      return (
                        <SidebarMenuItem
                          className="z-10"
                          key={item.title}
                          ref={(el) => (itemsRef.current[index] = el)}
                        >
                          <SidebarMenuButton
                            asChild
                            tooltip={item.title}
                          >
                            <button
                              onClick={() => {
                                toggleChat()
                                handleLinkClick(index)
                              }}
                              className={`bg-transparent px-4 py-6 transition-all duration-300 ${
                                isChatOpen
                                  ? "bg-[#2b2b2b] font-semibold text-primary"
                                  : "text-muted-foreground hover:bg-surface-hover hover:text-foreground"
                              }`}
                            >
                              <i
                                className={`align-center fi flex fi-${
                                  isChatOpen ? "ss-" : "ts-"
                                }${item.icon} text-lg`}
                              />
                              {!collapsed && <span className="ml-1">{item.title}</span>}
                            </button>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      )
                    }

                    return (
                      <SidebarMenuItem
                        className="z-10"
                        key={item.title}
                        ref={(el) => (itemsRef.current[index] = el)}
                      >
                        <SidebarMenuButton
                          asChild
                          tooltip={item.title}
                        >
                          <NavLink
                            to={item.url!}
                            end
                            onClick={() => handleLinkClick(index)}
                            className={`bg-transparent px-4 py-6 transition-all duration-300 ${checkIsActive(item.url!) ? "border-t border-t-[#555] bg-zinc-800/80 !text-orange-500 backdrop-blur-sm" : ""} ${getNavCls(item.url!)} `}
                          >
                            <i
                              className={`align-center fi flex transition-colors fi-${
                                checkIsActive(item.url!) ? "ss-" : "ts-"
                              }${item.icon} text-lg`}
                            />
                            {!collapsed && <span className="ml-1">{item.title}</span>}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
              <div
                style={{
                  transform: `translateY(${indicatorStyle.top}px)`,
                  height: `${indicatorStyle.height}px`,
                }}
                className="pointer-events-none absolute inset-x-4 top-0 z-0 hidden h-5 w-[calc(100%-32px)] overflow-hidden rounded-lg border-t border-t-[#555] bg-gradient-to-t from-[#222]/30 to-[#444]/20 shadow-md backdrop-blur-sm transition-all duration-500 sm:block"
              >
                <div className="size-full bg-[url('./assets/grain-texture.png')] bg-cover bg-no-repeat opacity-15" />
              </div>
            </SidebarGroup>
          </div>

          {/* User button at the bottom */}
          <div className="hidden p-4 group-data-[collapsible=icon]:px-[4px] md:block">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`transition-smooth flex w-full items-center gap-3 rounded-lg bg-surface-elevated px-3 py-2 hover:bg-surface-elevated group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:hover:bg-surface-elevated ${
                    collapsed ? "justify-center" : ""
                  }`}
                >
                  <div className="flex size-10 items-center justify-center rounded-full">
                    {user.pesFotoUrl ? (
                      <img
                        src={user.pesFotoUrl}
                        alt={user.pesNome}
                        className="size-full rounded-full object-cover"
                      />
                    ) : (
                      <i className="fi fi-ts-user flex text-2xl text-foreground sm:text-3xl" />
                    )}
                  </div>
                  {!collapsed && (
                    <div className="grow text-left">
                      <p className="text-sm font-medium text-foreground">{userName}</p>
                      <p className="text-xs capitalize text-muted-foreground">{userRole}</p>
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                side="right"
                className="mb-2 w-48"
              >
                <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    navigate("/profile")
                  }}
                >
                  <i className="fi fi-ts-circle-user mr-2 flex items-center justify-center"></i>
                  Meu Perfil
                </DropdownMenuItem>
                {user.pesTipo === 3 && (
                  <DropdownMenuItem
                    onClick={() => {
                      navigate("/company-profile")
                    }}
                  >
                    <i className="fi fi-ts-building mr-2 flex items-center justify-center"></i>
                    Perfil da Empresa
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => {
                    setIsFeedbackModalOpen(true)
                  }}
                >
                  <i className="fi fi-ts-skill-user mr-2 flex items-center justify-center"></i>
                  Enviar Feedback
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-400"
                >
                  <i className="fi fi-ts-sign-out-alt mr-2"></i>
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </SidebarContent>
      </Sidebar>
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
      />
    </>
  )
}
